import { useEffect, useRef, useState } from 'react';
import * as S from './Exam.styles';
import { QuestionHeader } from './QuestionHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { DrawerListQuestion } from './DrawerListQuestion';
import { LeftArrowIcon } from '../common/icons/LeftArrowIcon';
import { RightArrowIcon } from '../common/icons/RightArrowIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@app/store/store';
import { fetchExamDetail, fetchExamList, setTimePractice } from '@app/store/slices/examSlice';
import { RenderQuestion } from './renderQuestion';
import { HistoryPractice } from './HistoryPractice';
import { convertTypeParctice } from '@app/utils/utils';
import { TimerPractice, TimerPracticeRef } from './TimerPractice';
import { useTranslation } from 'react-i18next';

export const Exam: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    loadingDetail,
    listExam,
    error,
    currentQuestion,
    currentIndex,
    page,
    totalPages,
    searchCode,
    repeated,
    difficult,
    done,
  } = useSelector((state: RootState) => state.exam);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get('type');
  const questionId = queryParams.get('id');
  const pageFromUrl = queryParams.get('page');

  const [open, setOpen] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [again, setAgain] = useState(false);
  const [preBtnPrevious, setPreBtnPrevious] = useState(false);
  const isFirstRender = useRef(true);
  const [isLoadQuestion, setIsLoadQuestion] = useState(true);
  const [isSubmit, setIsSubmit] = useState(true);
  const [reloadHis, setReloadHis] = useState(false);
  const timerRef = useRef<TimerPracticeRef>(null);

  useEffect(() => {
    if (!type) return;

    const pageToUse = pageFromUrl ? parseInt(pageFromUrl) : page;
    if (pageToUse) {
      loadListQuestion(true);
      dispatch(fetchExamList({ page: pageToUse, type, searchCode, repeated, difficult, done }));
    }
  }, [pageFromUrl, dispatch]);

  useEffect(() => {
    if (listExam && listExam.length > 0 && isLoadQuestion) {
      retry();
      const targetQuestionId = preBtnPrevious ? listExam[listExam.length - 1].id : questionId || listExam[0].id;

      dispatch(fetchExamDetail(targetQuestionId));
      setPreBtnPrevious(false);
    }
  }, [listExam]);

  useEffect(() => {
    retry();
    if (!listExam.length || !questionId) return;
    const targetQuestionId = questionId;

    dispatch(fetchExamDetail(targetQuestionId));
    setPreBtnPrevious(false);
  }, [questionId]);

  const navigateToQuestion = (direction: 'next' | 'previous') => {
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    let newPage = page;

    if (newIndex < 0) {
      newPage = page - 1;
    } else if (newIndex >= listExam.length) {
      newPage = page + 1;
    }

    if (newPage !== page) {
      // dispatch(setPage(newPage));
      navigate(`/exam?type=${type}&page=${newPage}`);
      if (direction == 'previous') {
        setPreBtnPrevious(true);
      }
    } else if (newIndex >= 0 && newIndex < listExam.length) {
      navigate(`/exam?type=${type}&id=${listExam[newIndex].id}&page=${page}`);
    }
  };

  const handleNext = () => {
    setIsSubmit(true);
    setReloadHis(false);
    return navigateToQuestion('next');
  };

  const handlePrevious = () => {
    setIsSubmit(true);
    setReloadHis(false);
    return navigateToQuestion('previous');
  };

  const retry = () => {
    setShowAnswer(false);
    setAgain(false);
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  const handleAnswer = () => {
    handleGetTime();
  };

  const handleGetTime = () => {
    if (timerRef.current) {
      const currentTime = timerRef.current.getTime();
      dispatch(setTimePractice(currentTime));
      setShowAnswer(true);
      setAgain(true);
      setReloadHis(true);
    }
  };

  const loadListQuestion = (flag: boolean) => {
    setIsLoadQuestion(flag);
  };

  const handleAnswerChange = (answer: string | Blob) => {
    setIsSubmit(!answer ? true : false);
  };

  if (error) return <p style={{ color: 'red' }}>{`Xảy ra lỗi. Vui lòng thử lại sau`}</p>;

  if (loadingDetail)
    return (
      <div className="flex items-center justify-center">
        <Spin />
      </div>
    );

  return (
    <>
      {currentQuestion && (
        <>
          <S.Header>
            <S.Title level={4}>{convertTypeParctice(currentQuestion.type)}</S.Title>
            <TimerPractice ref={timerRef} />
          </S.Header>
          <S.Wrapper>
            {/* Question Header */}
            <QuestionHeader
              orderId={currentQuestion?.orderId}
              difficulty={currentQuestion?.difficulty}
              type={type || ''}
            />
            {/* End Question Header */}

            {/* Question */}
            <div className="mt-5">
              {type && (
                <>
                  <RenderQuestion
                    type={type}
                    currentQuestion={currentQuestion}
                    showAnswer={showAnswer}
                    isFirstRender={isFirstRender}
                    canStart={true}
                    allowDrag={true}
                    onAnswerChange={handleAnswerChange}
                    allowClipboardActions={true}
                  />
                  <HistoryPractice type={type} reload={reloadHis} />
                </>
              )}
            </div>
            {/* End Question */}
          </S.Wrapper>
          {/* Footer */}
          <S.Footer>
            <Button onClick={handlePrevious} disabled={currentIndex === 0 && page === 1}>
              {t('common.previous')}
            </Button>
            {!again ? (
              <div className="flex gap-3">
                <Button type="default" onClick={handleAnswer} disabled={isSubmit}>
                  {t('common.submit')}
                </Button>
                <Button type="default" onClick={handleTryAgain}>
                  {t('common.redo')}
                </Button>
              </div>
            ) : (
              <Button type="default" onClick={handleTryAgain}>
                {t('common.tryAgain')}
              </Button>
            )}

            <Button onClick={handleNext} disabled={currentIndex === listExam.length - 1 && page === totalPages}>
              {t('common.next')}
            </Button>
          </S.Footer>
          {/* End Footer */}
          <S.ToggleBtn>
            <Button
              type="primary"
              shape="circle"
              icon={open ? <RightArrowIcon /> : <LeftArrowIcon />}
              onClick={() => setOpen(true)}
              className="flex items-center justify-center"
            ></Button>
          </S.ToggleBtn>
          <DrawerListQuestion
            open={open}
            toggle={() => {
              setOpen(false);
            }}
            type={type || ''}
            loadListQuestion={loadListQuestion}
          />
        </>
      )}
    </>
  );
};
